import React, { useEffect, useMemo, useRef, useState } from "react";
import { URL } from "../../utils/constants";
import { Link } from "react-router-dom";
import "./OCKCloud.css";
import { toast } from "../map/toast";
import OCKToasts from "./OCKToasts";

function normalizeSlashes(value) {
  return String(value || "")
    .replace(/\\/g, "/")
    .replace(/\/+/g, "/")
    .replace(/^\/|\/$/g, "");
}

function splitPath(path) {
  const normalized = normalizeSlashes(path);
  return normalized ? normalized.split("/") : [];
}

function joinPath(parts) {
  return normalizeSlashes(parts.join("/"));
}

function getParentPath(path, rootPath) {
  const parts = splitPath(path);
  if (parts.length <= 1) return rootPath;
  return joinPath(parts.slice(0, -1));
}

function getBaseName(path) {
  const parts = splitPath(path);
  return parts[parts.length - 1] || "";
}

function buildObjectFolderName(object) {
  if (object?.cloud_folder_name) {
    return normalizeSlashes(object.cloud_folder_name);
  }

  if (object?.id && object?.name) {
    return normalizeSlashes(`${object.id}_${object.name}`);
  }

  return normalizeSlashes(String(object?.name || "").trim());
}

function buildObjectFolderPath(object, rootPath) {
  if (object?.cloud_folder_path) {
    return normalizeSlashes(object.cloud_folder_path);
  }

  return joinPath([rootPath, buildObjectFolderName(object)]);
}

function formatFolderDisplayName(name) {
  const map = {
    Metro: "Метро",
    OCK: "ОСК",
  };

  return map[name] || name;
}

function buildBreadcrumbs(path) {
  const parts = splitPath(path);

  return parts.map((part, idx) => ({
    name: formatFolderDisplayName(part),
    path: joinPath(parts.slice(0, idx + 1)),
  }));
}

function buildApiUrl(pathOrUrl) {
  const value = String(pathOrUrl || "").trim();

  if (!value) return "";

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  const base = String(URL || "").replace(/\/+$/g, "");
  const path = value.startsWith("/") ? value : `/${value}`;

  return `${base}${path}`;
}

function isDirectChild(parentPath, childPath) {
  const parent = splitPath(parentPath);
  const child = splitPath(childPath);

  if (child.length !== parent.length + 1) return false;
  return parent.every((part, idx) => child[idx] === part);
}

function buildFolderTreeFromObjects(objects, rootPath) {
  const objectFolders = (objects || [])
    .map((obj) => {
      const fullPath = buildObjectFolderPath(obj, rootPath);
      const folderName = getBaseName(fullPath);

      return {
        type: "folder",
        id: `folder:${fullPath}`,
        name: folderName,
        displayName: obj.name || folderName,
        path: fullPath,
        objectId: obj.id,
        storageType: "ock",
        children: [],
      };
    })
    .sort((a, b) => String(a.displayName || a.name).localeCompare(String(b.displayName || b.name), "ru"));

  return {
    root: {
      type: "folder",
      id: `folder:${rootPath}`,
      name: getBaseName(rootPath),
      displayName: getBaseName(rootPath),
      path: rootPath,
      storageType: "ock",
      children: objectFolders,
    },
    flatFolders: [
      {
        type: "folder",
        id: `folder:${rootPath}`,
        name: rootPath,
        path: rootPath,
      },
      ...objectFolders,
    ],
  };
}

function buildFolderTreeFromMetroFolders(folders, rootPath) {
  const root = {
    type: "folder",
    id: `folder:${rootPath}`,
    name: getBaseName(rootPath),
    displayName: "Метро",
    path: rootPath,
    folderId: null,
    storageType: "metro",
    children: [],
  };

  const byId = new Map();
  const flatFolders = [
    {
      type: "folder",
      id: `folder:${rootPath}`,
      name: rootPath,
      displayName: rootPath,
      path: rootPath,
      folderId: null,
    },
  ];

  const normalized = (folders || [])
    .map((folder) => ({
      type: "folder",
      id: `folder:${folder.id}`,
      name: folder.name || "Без названия",
      displayName: folder.name || "Без названия",
      path: "",
      folderId: folder.id,
      parent: folder.parent ?? null,
      sort_order: folder.sort_order ?? 0,
      children: [],
      raw: folder,
    }))
    .sort((a, b) => {
      const bySort = Number(a.sort_order) - Number(b.sort_order);
      if (bySort !== 0) return bySort;
      return String(a.displayName).localeCompare(String(b.displayName), "ru");
    });

  normalized.forEach((folder) => byId.set(String(folder.folderId), folder));

  const makePath = (folder) => {
    const chain = [];
    let current = folder;

    while (current) {
      chain.unshift(current.name);
      current = current.parent ? byId.get(String(current.parent)) : null;
    }

    return joinPath([rootPath, ...chain]);
  };

  normalized.forEach((folder) => {
    folder.path = makePath(folder);

    flatFolders.push({
      type: "folder",
      id: `folder:${folder.path}`,
      name: folder.name,
      displayName: folder.displayName,
      path: folder.path,
      folderId: folder.folderId,
      parent: folder.parent,
      raw: folder.raw,
    });
  });

  normalized.forEach((folder) => {
    if (folder.parent) {
      const parent = byId.get(String(folder.parent));
      if (parent) parent.children.push(folder);
      else root.children.push(folder);
    } else {
      root.children.push(folder);
    }
  });

  return { root, flatFolders };
}

function buildUnifiedFolderTree(objects, metroFolders) {
  const ockRootPath = joinPath([ROOT_PATH, "OCK"]);
  const metroRootPath = joinPath([ROOT_PATH, "Metro"]);

  const ockTree = buildFolderTreeFromObjects(objects, ockRootPath);
  const metroTree = buildFolderTreeFromMetroFolders(metroFolders, metroRootPath);

  const root = {
    type: "folder",
    id: `folder:${ROOT_PATH}`,
    name: ROOT_PATH,
    displayName: ROOT_PATH,
    path: ROOT_PATH,
    storageType: null,
    children: [
      {
        ...ockTree.root,
        name: "OCK",
        displayName: "OCK",
        storageType: "ock",
      },
      {
        ...metroTree.root,
        name: "Metro",
        displayName: "Метро",
        storageType: "metro",
      },
    ],
  };

  const flatFolders = [
    {
      type: "folder",
      id: `folder:${ROOT_PATH}`,
      name: ROOT_PATH,
      displayName: ROOT_PATH,
      path: ROOT_PATH,
      storageType: null,
    },
    ...ockTree.flatFolders.map((f) => ({
      ...f,
      storageType: "ock",
    })),
    ...metroTree.flatFolders.map((f) => ({
      ...f,
      storageType: "metro",
    })),
  ];

  return { root, flatFolders };
}

function getDirectoryEntries(currentPath, docLinks, metroFiles, folders) {
  const current = normalizeSlashes(currentPath);

  const childFolders = folders
    .filter((f) => isDirectChild(current, f.path))
    .map((f) => ({
      type: "folder",
      id: `folder:${f.path}`,
      name: f.displayName || formatFolderDisplayName(f.name),
      path: f.path,
      updated_at: "",
      uploaded_by_username: "",
      size: null,
      objectId: f.objectId ?? null,
      folderId: f.folderId ?? null,
      storageType: f.storageType ?? null,
    }));

  const currentFolder = folders.find(
    (f) => normalizeSlashes(f.path) === current
  );

  const currentStorageType = currentFolder?.storageType ?? null;
  const currentObjectId = currentFolder?.objectId ?? null;
  const currentFolderId = currentFolder?.folderId ?? null;

  let childFiles = [];

  if (currentStorageType === "metro" && currentFolderId) {
    childFiles = metroFiles
      .filter((file) => String(file.folder) === String(currentFolderId))
      .map((file) => {
        const name = file.name || file.original_filename || "Без названия";
        const virtualPath = joinPath([current, name]);

        return {
          type: "file",
          id: `metro-file:${file.id}`,
          storageType: "metro",
          docId: file.id,
          name,
          path: virtualPath,
          previewUrl: file.preview_url,
          downloadUrl: file.download_url,
          canPreview: file.can_preview,
          updated_at: file.updated_at,
          created_at: file.created_at,
          uploaded_by_username: file.uploaded_by_username,
          uploaded_by_id: file.uploaded_by_id,
          size: file.size ?? null,
          raw: file,
        };
      });
  }

  if (currentStorageType === "ock" && currentObjectId) {
    childFiles = docLinks
      .filter((link) => String(link.object_id) === String(currentObjectId))
      .map((link) => {
        const name =
          link.display_name ||
          link.name ||
          link.document_name ||
          "Без названия";

        const virtualPath = joinPath([current, name]);

        return {
          type: "file",
          id: `ock-file:${link.link_id || link.id}`,
          storageType: "ock",
          linkId: link.link_id || link.id,
          docId: link.document_id,
          objectId: link.object_id,
          name,
          path: virtualPath,
          previewUrl: link.preview_url,
          downloadUrl: link.download_url,
          canPreview: link.can_preview,
          updated_at: link.updated_at,
          created_at: link.created_at,
          uploaded_by_username: link.uploaded_by_username,
          uploaded_by_id: link.uploaded_by_id,
          size: link.size ?? null,
          raw: link,
        };
      });
  }

  return [...childFolders, ...childFiles].sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return String(a.name || "").localeCompare(String(b.name || ""), "ru");
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatSize(size) {
  if (size === null || size === undefined || Number.isNaN(Number(size))) return "—";
  const bytes = Number(size);

  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} ГБ`;
}

function getFilenameFromContentDisposition(header) {
  if (!header) return "";

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const normalMatch = header.match(/filename="?([^"]+)"?/i);
  return normalMatch?.[1] || "";
}

function getPreviewMimeType(entry, response) {
  const headerType = response.headers.get("Content-Type");

  if (headerType && !headerType.includes("application/octet-stream")) {
    return headerType;
  }

  const name = String(entry.name || entry.raw?.document_name || "").toLowerCase();
  const ext = String(entry.raw?.file_ext || "").toLowerCase();

  const value = ext || name.slice(name.lastIndexOf("."));

  const map = {
    ".pdf": "application/pdf",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".webp": "image/webp",
    ".txt": "text/plain;charset=utf-8",
  };

  return map[value] || headerType || "application/octet-stream";
}

function FolderTreeNode({
  node,
  currentPath,
  expandedPaths,
  onToggle,
  onOpen,
  level = 0,
}) {
  const isActive = normalizeSlashes(node.path) === normalizeSlashes(currentPath);
  const isExpanded = expandedPaths.has(node.path);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  return (
    <div className="ock-cloud__treeNode">
      <div
        className={`ock-cloud__treeRow ${isActive ? "ock-cloud__treeRow--active" : ""}`}
        style={{ paddingLeft: `${level * 14}px` }}
      >
        <button
          type="button"
          className="ock-cloud__treeToggle"
          onClick={() => hasChildren && onToggle(node.path)}
          disabled={!hasChildren}
          title={hasChildren ? "Развернуть / свернуть" : ""}
        >
          {hasChildren ? (isExpanded ? "▾" : "▸") : "•"}
        </button>

        <button
          type="button"
          className="ock-cloud__treeOpen"
          onClick={() => onOpen(node.path)}
          title={node.path}
        >
          <span className="ock-cloud__treeFolderIcon">📁</span>
          <span className="ock-cloud__treeName">
            {node.displayName || formatFolderDisplayName(node.name)}
          </span>
        </button>
      </div>

      {hasChildren && isExpanded ? (
        <div className="ock-cloud__treeChildren">
          {node.children.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              currentPath={currentPath}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              onOpen={onOpen}
              level={level + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const ROOT_PATH = "Облако";

export default function OCKCloud({ user, tokenProp }) {
  const config = {
    title: "Облако документации",
    sidebarTitle: "Разделы",
    trashPath: "/cloud-trash",
  };

  const [docLinks, setDocLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canUsePanel, setCanUsePanel] = useState(false);

  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [contextMenu, setContextMenu] = useState(null);

  const [query, setQuery] = useState("");
  const [currentPath, setCurrentPath] = useState(ROOT_PATH);
  const [expandedPaths, setExpandedPaths] = useState(
    new Set([ROOT_PATH, "Облако/OCK", "Облако/Metro"])
  );
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [objects, setObjects] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [metroFolders, setMetroFolders] = useState([]);
  const [metroFiles, setMetroFiles] = useState([]);
  const [creatingFolder, setCreatingFolder] = useState(false);
  
  const fileInputRef = useRef(null);

  const canView = canUsePanel;
  const canManage = canUsePanel;

  const getToken = () => tokenProp || localStorage.getItem("auth_token");

  useEffect(() => {
    let cancelled = false;

    const loadObjects = async () => {
      const token = tokenProp || localStorage.getItem("auth_token");
      if (!token || !canView) return;

      try {
        const res = await fetch(`${URL}/api/ock-objects/`, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) {
          throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
        }

        const items = Array.isArray(data) ? data : data.items || data.results || [];

        const normalizedItems = items.filter(
          (obj) => String(obj.name || "").trim() !== ""
        );

        if (!cancelled) setObjects(normalizedItems);
      } catch (e) {
        console.error("Failed to load OCK objects:", e);
        if (!cancelled) setObjects([]);
      }
    };

    if (canView) loadObjects();

    return () => {
      cancelled = true;
    };
  }, [canView, tokenProp]);

  useEffect(() => {
    let cancelled = false;

    const loadMetroFolders = async () => {
      const token = tokenProp || localStorage.getItem("auth_token");
      if (!token || !canView) return;

      try {
        const res = await fetch(`${URL}/api/metro-doc-folders/?is_archived=false`, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) {
          throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
        }

        const items = Array.isArray(data) ? data : data.items || data.results || [];
        if (!cancelled) setMetroFolders(items);
      } catch (e) {
        console.error("Failed to load Metro folders:", e);
        if (!cancelled) setMetroFolders([]);
      }
    };

    if (canView) loadMetroFolders();

    return () => {
      cancelled = true;
    };
  }, [canView, tokenProp]);

  useEffect(() => {
    let mounted = true;

    const norm = (s) => String(s ?? "").toLowerCase().trim();

    const applyFromObj = (u = {}) => {
      let groups = u.groups || u.group_names || [];

      if (Array.isArray(groups) && groups.length && typeof groups[0] === "object") {
        groups = groups.map((g) => g?.name);
      }

      const normalizedGroups = (groups || []).map(norm);
      const hasAccess =
        normalizedGroups.includes("ock") ||
        normalizedGroups.includes("hto");

      if (mounted) setCanUsePanel(hasAccess);
      return hasAccess;
    };

    try {
      const raw = localStorage.getItem("user");
      const parsed = JSON.parse(raw || "{}");
      if (parsed && (parsed.id || parsed.pk || (parsed.user && parsed.user.id))) {
        applyFromObj(parsed);
      }
    } catch (e) {
      console.warn("Не удалось прочитать user из localStorage:", e);
    }

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      if (mounted) setLoading(false);
      return () => {
        mounted = false;
      };
    }

    const endpoints = [
      `${URL}/api/me/`,
      `${URL}/api/users/me/`,
      `${URL}/users/me/`,
      `${URL}/api/auth/users/me/`,
    ];

    (async () => {
      for (const ep of endpoints) {
        try {
          const r = await fetch(ep, {
            headers: { Authorization: `Token ${token}` },
          });

          if (!r.ok) continue;

          const data = await r.json();
          applyFromObj(data);
          break;
        } catch (e) {
          console.warn("Ошибка получения профиля:", ep, e);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [tokenProp, user]);

  useEffect(() => {
    let cancelled = false;

    const loadFiles = async () => {
      const token = tokenProp || localStorage.getItem("auth_token");

      if (!token || !canView) {
        if (!cancelled) {
          setLoading(false);
          setDocLinks([]);
          setMetroFiles([]);
        }
        return;
      }

      setLoading(true);

      try {
        const [ockRes, metroRes] = await Promise.all([
          fetch(`${URL}/api/ock-object-doc-links/?is_archived=false`, {
            headers: { Authorization: `Token ${token}` },
          }),
          fetch(`${URL}/api/metro-doc-files/?is_deleted=false`, {
            headers: { Authorization: `Token ${token}` },
          }),
        ]);

        const ockData = await ockRes.json().catch(() => []);
        const metroData = await metroRes.json().catch(() => []);

        if (!ockRes.ok) {
          throw new Error(typeof ockData === "object" ? JSON.stringify(ockData) : `HTTP ${ockRes.status}`);
        }

        if (!metroRes.ok) {
          throw new Error(typeof metroData === "object" ? JSON.stringify(metroData) : `HTTP ${metroRes.status}`);
        }

        const ockItems = Array.isArray(ockData) ? ockData : ockData.items || ockData.results || [];
        const metroItems = Array.isArray(metroData) ? metroData : metroData.items || metroData.results || [];

        if (cancelled) return;

        setDocLinks(ockItems);
        setMetroFiles(metroItems);
      } catch (e) {
        console.error("Failed to load document files:", e);

        if (!cancelled) {
          setDocLinks([]);
          setMetroFiles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (canView) {
      loadFiles();
    } else {
      setLoading(false);

      setDocLinks([]);
      setMetroFiles([]);
    }

    return () => {
      cancelled = true;
    };
  }, [canView, tokenProp]);

  const { root: folderTree, flatFolders } = useMemo(
    () => buildUnifiedFolderTree(objects, metroFolders),
    [objects, metroFolders]
  );

  const breadcrumbs = useMemo(() => buildBreadcrumbs(currentPath), [currentPath]);

  const currentObject = useMemo(() => {
    const normalizedPath = normalizeSlashes(currentPath);

    const folder = flatFolders.find(
      (f) => normalizeSlashes(f.path) === normalizedPath
    );

    if (!folder?.objectId) return null;

    return objects.find((obj) => String(obj.id) === String(folder.objectId)) || null;
  }, [objects, flatFolders, currentPath]);

  const currentFolder = useMemo(() => {
    const normalizedPath = normalizeSlashes(currentPath);

    return flatFolders.find(
      (f) => normalizeSlashes(f.path) === normalizedPath
    ) || null;
  }, [flatFolders, currentPath]);

  const currentObjectId = currentObject?.id ?? null;
  const currentFolderId = currentFolder?.folderId ?? null;
  const currentStorageType = currentFolder?.storageType ?? null;

  const canUploadHere =
    currentStorageType === "metro"
      ? Boolean(currentFolderId)
      : currentStorageType === "ock"
        ? Boolean(currentObject)
        : false;

  const openUploadDialog = () => {
    if (!canUploadHere) {
      toast.warn(
        currentStorageType === "metro"
          ? "Сначала откройте папку Метро для загрузки"
          : currentStorageType === "ock"
            ? "Загрузка доступна только внутри папки конкретного объекта OCK"
            : "Откройте папку, в которую можно загрузить файл"
      );
      return;
    }

    if (uploading) return;
    fileInputRef.current?.click();
  };

  const allEntries = useMemo(
    () => getDirectoryEntries(currentPath, docLinks, metroFiles, flatFolders),
    [currentPath, docLinks, metroFiles, flatFolders]
  );

  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allEntries;

    return allEntries.filter((entry) => {
      const name = String(entry.name || "").toLowerCase();
      const path = String(entry.path || "").toLowerCase();
      return name.includes(q) || path.includes(q);
    });
  }, [allEntries, query]);

  const selectedEntries = useMemo(
    () => filteredEntries.filter((entry) => selectedIds.has(entry.id)),
    [filteredEntries, selectedIds]
  );

  const visibleFiles = useMemo(
    () => filteredEntries.filter((entry) => entry.type === "file"),
    [filteredEntries]
  );

  const allVisibleSelected =
    visibleFiles.length > 0 && visibleFiles.every((entry) => selectedIds.has(entry.id));

  const toggleExpand = (path) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const openFolder = (path) => {
    const normalized = normalizeSlashes(path);
    setCurrentPath(normalized);
    setExpandedPaths((prev) => new Set([...prev, normalized, getParentPath(normalized, ROOT_PATH)]));
    setSelectedIds(new Set());
  };

  const goUp = () => {
    if (normalizeSlashes(currentPath) === ROOT_PATH) return;
    openFolder(getParentPath(currentPath, ROOT_PATH));
  };

  const clearSearch = () => setQuery("");

  const toggleSelect = (entryId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allVisibleSelected) {
        visibleFiles.forEach((entry) => next.delete(entry.id));
      } else {
        visibleFiles.forEach((entry) => next.add(entry.id));
      }

      return next;
    });
  };

  const handlePreview = async (entry) => {
    if (entry.type !== "file") return;

    if (!entry.docId) {
      toast.error("Не удалось определить ID документа для просмотра");
      console.error("Preview failed: empty docId", entry);
      return;
    }

    const token = getToken();
    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    try {
      const previewEndpoint =
        entry.storageType === "metro"
          ? `/api/metro-doc-files/${entry.docId}/preview/`
          : `/api/ock-object-docs/${entry.docId}/preview/`;

      const finalUrl = buildApiUrl(previewEndpoint);

      console.log("Preview URL:", finalUrl, entry);

      const res = await fetch(finalUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      const rawBlob = await res.blob();
      const mimeType = getPreviewMimeType(entry, res);

      const previewBlob = new Blob([rawBlob], {
        type: mimeType,
      });

      const blobUrl = window.URL.createObjectURL(previewBlob);

      window.open(blobUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60_000);
    } catch (e) {
      console.error("Preview failed:", e);
      toast.error("Ошибка просмотра файла");
    }
  };

  const handleDownload = async (entry) => {
    if (entry.type !== "file") return;

    if (!entry.docId) {
      toast.error("Не удалось определить ID документа для скачивания");
      console.error("Download failed: empty docId", entry);
      return;
    }

    const token = getToken();
    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    try {
      const downloadEndpoint =
        entry.storageType === "metro"
          ? `/api/metro-doc-files/${entry.docId}/download/`
          : `/api/ock-object-docs/${entry.docId}/download/`;

      const finalUrl = buildApiUrl(downloadEndpoint);

      console.log("Download URL:", finalUrl, entry);

      const res = await fetch(finalUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      const blob = await res.blob();

      const headerFilename = getFilenameFromContentDisposition(
        res.headers.get("Content-Disposition")
      );

      const filename = headerFilename || entry.name || "document";

      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Download failed:", e);
      toast.error("Ошибка скачивания файла");
    }
  };

  const handleDownloadSelected = async () => {
    const files = selectedEntries.filter((entry) => entry.type === "file");

    if (!files.length) {
      toast.warn("Выберите хотя бы один файл");
      return;
    }

    toast.info(`Скачивание файлов: ${files.length}`);

    for (const entry of files) {
      await handleDownload(entry);
    }
  };

  const handleDeleteSingle = async (entry) => {
    if (!canManage || entry.type !== "file") return;

    const ok = await toast.confirm(`Удалить файл "${entry.name}" в корзину? Он исчезнет из всех объектов, где прикреплен.`);
    if (!ok) return;

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    try {
      const deleteEndpoint =
        entry.storageType === "metro"
          ? `${URL}/api/metro-doc-files/${entry.docId}/`
          : `${URL}/api/ock-object-docs/${entry.docId}/`;

      const res = await fetch(deleteEndpoint, {
        method: "DELETE",
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
      }

      if (entry.storageType === "metro") {
        setMetroFiles((prev) =>
          prev.filter((file) => String(file.id) !== String(entry.docId))
        );
      } else {
        setDocLinks((prev) =>
          prev.filter((link) => String(link.document_id) !== String(entry.docId))
        );
      }

      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
      toast.success("Файл удалён");
    } catch (e) {
      console.error("Delete failed:", e);
      toast.error("Ошибка удаления файла");
    }
  };

  const startRename = (entry) => {
    if (!canManage || entry.type !== "file") return;
    setRenamingId(entry.id);
    setRenameValue(entry.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const commitRename = async (entry) => {
    const trimmedName = renameValue.trim();

    if (!trimmedName || trimmedName === entry.name) {
      cancelRename();
      return;
    }

    const token = getToken();

    if (!token) {
      toast.warn("Нет токена авторизации");
      cancelRename();
      return;
    }

    try {
      const renameUrl =
        entry.storageType === "metro"
          ? `${URL}/api/metro-doc-files/${entry.docId}/`
          : `${URL}/api/ock-object-doc-links/${entry.linkId}/`;

      const renameBody =
        entry.storageType === "metro"
          ? { name: trimmedName }
          : { display_name: trimmedName };

      const res = await fetch(renameUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify(renameBody),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Rename API error:", res.status, data);
        throw new Error(`HTTP ${res.status}`);
      }

      if (entry.storageType === "metro") {
        setMetroFiles((prev) =>
          prev.map((file) =>
            String(file.id) === String(entry.docId)
              ? {
                  ...file,
                  name: data.name ?? trimmedName,
                  updated_at: data.updated_at ?? file.updated_at,
                }
              : file
          )
        );
      } else {
        setDocLinks((prev) =>
          prev.map((link) => {
            const linkId = link.link_id || link.id;

            if (String(linkId) !== String(entry.linkId)) return link;

            return {
              ...link,
              display_name: data.display_name ?? trimmedName,
              name: data.name ?? data.display_name ?? trimmedName,
              updated_at: data.updated_at ?? link.updated_at,
            };
          })
        );
      }

      toast.success("Имя файла обновлено");
    } catch (e) {
      console.error("Rename failed:", e);
      toast.error("Ошибка переименования файла");
    } finally {
      cancelRename();
    }
  };

  const handleCreateMetroFolder = async () => {
    if (currentStorageType  !== "metro" || !canManage) return;

    const name = window.prompt("Название папки");
    if (!name?.trim()) return;

    const token = getToken();
    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    setCreatingFolder(true);

    try {
      const res = await fetch(`${URL}/api/metro-doc-folders/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          parent: currentFolderId || null,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
      }

      setMetroFolders((prev) => [...prev, data]);
      setExpandedPaths((prev) => new Set([...prev, currentPath]));
      toast.success("Папка создана");
    } catch (e) {
      console.error("Create folder failed:", e);
      toast.error("Ошибка создания папки");
    } finally {
      setCreatingFolder(false);
    }
  };

  const openContextMenu = (e, entry) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      entry,
    });
  };

  const closeContextMenu = () => setContextMenu(null);

  useEffect(() => {
    if (!contextMenu) return;

    const close = () => closeContextMenu();
    const esc = (e) => {
      if (e.key === "Escape") closeContextMenu();
    };

    window.addEventListener("click", close);
    window.addEventListener("contextmenu", close);
    window.addEventListener("keydown", esc);

    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", esc);
    };
  }, [contextMenu]);

  const handleBulkDelete = async () => {
    if (!canManage) return;

    const files = selectedEntries.filter((entry) => entry.type === "file");
    if (!files.length) {
      toast.warn("Выберите хотя бы один файл");
      return;
    }

    const uniqueDocIds = [...new Set(files.map((f) => String(f.docId)))];

    const ok = await toast.confirm(
      `Удалить выбранные файлы (${uniqueDocIds.length}) в корзину? Они исчезнут из всех объектов, где прикреплены.`
    );
    if (!ok) return;

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      toast.warn("Нет токена авторизации");
      return;
    }

    try {
      const uniqueDocIds = [...new Set(files.map((f) => String(f.docId)))];

      for (const entry of files) {
        const deleteUrl =
          entry.storageType === "metro"
            ? `${URL}/api/metro-doc-files/${entry.docId}/`
            : `${URL}/api/ock-object-docs/${entry.docId}/`;

        const res = await fetch(deleteUrl, {
          method: "DELETE",
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
        }
      }

      const deletedMetroIds = new Set(
        files.filter((f) => f.storageType === "metro").map((f) => String(f.docId))
      );

      const deletedOckIds = new Set(
        files.filter((f) => f.storageType === "ock").map((f) => String(f.docId))
      );

      setMetroFiles((prev) =>
        prev.filter((file) => !deletedMetroIds.has(String(file.id)))
      );

      setDocLinks((prev) =>
        prev.filter((link) => !deletedOckIds.has(String(link.document_id)))
      );

      setSelectedIds(new Set());
      toast.success(`Удалено файлов: ${files.length}`);
    } catch (e) {
      console.error("Bulk delete failed:", e);
      toast.error("Ошибка пакетного удаления");
    }
  };

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
  
    if (!files.length) return;
  
    if (!currentStorageType) {
      toast.warn("Откройте папку ОСК или Метро для загрузки");
      return;
    }

    if (currentStorageType === "metro" && !currentFolderId) {
      toast.warn("Не удалось определить папку Метро для загрузки");
      return;
    }

    if (currentStorageType !== "metro" && !currentObjectId) {
      toast.warn("Не удалось определить объект для загрузки");
      return;
    }
  
    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      toast.error("Нет токена авторизации");
      return;
    }
  
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
  
    setUploading(true);
    const toastId = toast.loading(`Загрузка файлов: ${files.length}`);
  
    try {
      const uploadEndpoint =
        currentStorageType === "metro"
          ? `${URL}/api/metro-doc-folders/${currentFolderId}/upload-files/`
          : `${URL}/api/ock-objects/${currentObjectId}/upload-docs/`;

      const res = await fetch(uploadEndpoint, {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
        },
        body: formData,
      });
  
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
      }
  
      const created = Array.isArray(data) ? data : data.items || data.results || [];
      if (currentStorageType === "metro") {
        setMetroFiles((prev) => [...created, ...prev]);
      } else {
        setDocLinks((prev) => [...created, ...prev]);
      }
      toast.update(toastId, `Загружено файлов: ${created.length}`, "success");
    } catch (err) {
      console.error("Upload failed:", err);
      toast.update(toastId, "Ошибка загрузки файлов", "error");
    } finally {
      setUploading(false);
    }
  };

  if (!canView) return <div>Нет доступа к разделу {config.title}</div>;
  if (loading) return <div>Загрузка раздела {config.title}...</div>;
  const isRootFolder = normalizeSlashes(currentPath) === ROOT_PATH;
  const canSelectFilesHere = !isRootFolder && visibleFiles.length > 0;

  return (
    <div className="ock-cloud">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileInputChange}
      />

      <div className="ock-cloud__headerCard">
        <div className="ock-cloud__bar">
          <button
            className="ock-cloud__barBtn"
            type="button"
            onClick={goUp}
            disabled={normalizeSlashes(currentPath) === ROOT_PATH}
            title="На уровень выше"
          >
            ↑
          </button>

          <nav className="ock-cloud__breadcrumbs">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={crumb.path}>
                {idx > 0 ? <span className="ock-cloud__crumbSep">/</span> : null}

                <button
                  type="button"
                  className="ock-cloud__crumb"
                  onClick={() => openFolder(crumb.path)}
                  title={crumb.path}
                >
                  {idx === 0 ? "Домой" : crumb.name}
                </button>
              </React.Fragment>
            ))}
          </nav>
        </div>

        <div className="ock-cloud__toolbar">
          <div className="ock-cloud__searchWrap">
            <div className="ock-cloud__searchBar">
              <input
                className="ock-cloud__search"
                placeholder="Поиск по имени или пути…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />

              <button
                className="ock-cloud__clearBtn"
                onClick={clearSearch}
                title="Очистить"
                type="button"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="ock-cloud__toolbarActions">
            {currentStorageType === "metro" && (
              <button
                className="ock-cloud__btn ock-cloud__btn--secondary"
                type="button"
                onClick={handleCreateMetroFolder}
                disabled={creatingFolder}
              >
                {creatingFolder ? "Создание..." : "Создать папку"}
              </button>
            )}

            <button
              className="ock-cloud__btn ock-cloud__btn--primary"
              type="button"
              onClick={openUploadDialog}
              disabled={!canUploadHere || uploading}
              title={
                canUploadHere
                  ? `Загрузить в объект: ${currentObject?.name || currentObjectId}`
                  : "Загрузка доступна только внутри папки объекта OCK"
              }
            >
              {uploading ? "Загрузка..." : "Загрузить"}
            </button>

            <button
              className="ock-cloud__btn ock-cloud__btn--secondary"
              type="button"
              onClick={handleDownloadSelected}
              disabled={!selectedEntries.some((e) => e.type === "file")}
              title="Скачать выбранные файлы"
            >
              Скачать выбранные
            </button>

            <button
              className="ock-cloud__btn ock-cloud__btn--danger"
              type="button"
              disabled={!selectedEntries.some((e) => e.type === "file")}
              onClick={handleBulkDelete}
            >
              Удалить выбранные
            </button>

            <button className="ock-cloud__btn ock-cloud__btn--secondary" type="button">
              <Link to="/cloud-trash" className="ock-link">
                Корзина
              </Link>
            </button>
          </div>
        </div>

        <div className="ock-cloud__submeta">
          <span className="ock-cloud__metaChip ock-cloud__metaChip--path" title={currentPath}>
            <span className="ock-cloud__metaLabel">Текущая папка</span>
            <b>{currentPath}</b>
          </span>

          <span className="ock-cloud__metaChip">
            <span className="ock-cloud__metaLabel">Элементов</span>
            <b>{filteredEntries.length}</b>
          </span>

          <span className="ock-cloud__metaChip">
            <span className="ock-cloud__metaLabel">Выбрано</span>
            <b>{selectedEntries.length}</b>
          </span>

          <span
            className="ock-cloud__metaChip ock-cloud__metaChip--object"
            title={currentObject ? currentObject.name : "не выбран"}
          >
            <span className="ock-cloud__metaLabel">Объект загрузки</span>
            <b>{currentObject ? currentObject.name : "не выбран"}</b>
          </span>
        </div>
      </div>

      <div className="ock-cloud__layout">
        <aside className="ock-cloud__sidebar">
          <div className="ock-cloud__panelTitle">{config.sidebarTitle}</div>

          <div className="ock-cloud__scroll">
            <FolderTreeNode
              node={folderTree}
              currentPath={currentPath}
              expandedPaths={expandedPaths}
              onToggle={toggleExpand}
              onOpen={openFolder}
            />
          </div>
        </aside>

        <section className="ock-cloud__content">
          {filteredEntries.length === 0 ? (
            <div className="ock-cloud__empty">В этой директории ничего не найдено</div>
          ) : (
            <div className="ock-cloud__scroll">
              {canSelectFilesHere ? (
                <div className="ock-cloud__listHead">
                  <label className="ock-cloud__selectAll">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                    />
                    <span>Выбрать всё</span>
                  </label>
                </div>
              ) : null}

              <div className="ock-cloud__list">
                {filteredEntries.map((entry) => {
                  const isSelected = selectedIds.has(entry.id);
                  const isFolder = entry.type === "folder";
                  const isRenaming = renamingId === entry.id;

                  return (
                    <div
                      key={entry.id}
                      className={`ock-cloud__item ${
                        isFolder ? "ock-cloud__item--folder" : "ock-cloud__item--file"
                      }`}
                      onContextMenu={(e) => openContextMenu(e, entry)}
                    >
                      <div className="ock-cloud__checkboxCol">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(entry.id)}
                          disabled={isFolder}
                        />
                      </div>

                      <button
                        className="ock-cloud__fileIcon"
                        type="button"
                        onClick={() => (isFolder ? openFolder(entry.path) : handlePreview(entry))}
                        title={isFolder ? "Открыть папку" : "Просмотр"}
                      >
                        {isFolder ? "📁" : "📄"}
                      </button>

                      <div className="ock-cloud__fileMain">
                        {isRenaming ? (
                          <input
                            className="ock-cloud__renameInput"
                            autoFocus
                            value={renameValue}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => commitRename(entry)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename(entry);
                              if (e.key === "Escape") cancelRename();
                            }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="ock-cloud__fileName"
                            onClick={() => (isFolder ? openFolder(entry.path) : handlePreview(entry))}
                            title={entry.name}
                          >
                            {entry.name}
                          </button>
                        )}

                        <div className="ock-cloud__fileInfo">
                          <span>{isFolder ? "Папка" : `Размер: ${formatSize(entry.size)}`}</span>

                          {!isFolder ? (
                            <>
                              <span>Загружен: {formatDateTime(entry.created_at)}</span>
                              <span>Обновлён: {formatDateTime(entry.updated_at)}</span>
                              <span>Пользователь: {entry.uploaded_by_username || "—"}</span>
                            </>
                          ) : (
                            <span>Директория-представление объекта OCK</span>
                          )}
                        </div>

                        <div className="ock-cloud__filePath">{entry.path}</div>
                      </div>

                      <div className="ock-cloud__iconActions">
                        {!isFolder && (
                          <>
                            <button
                              className="ock-cloud__iconBtn"
                              type="button"
                              onClick={() => startRename(entry)}
                              disabled={!canManage}
                              title="Переименовать"
                            >
                              ✏️
                            </button>

                            <button
                              className="ock-cloud__iconBtn ock-cloud__iconBtn--danger"
                              type="button"
                              onClick={() => handleDeleteSingle(entry)}
                              disabled={!canManage}
                              title="Удалить"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      {contextMenu && (
        <>
          <div
            className="ock-cloud__contextBackdrop"
            onClick={closeContextMenu}
            onContextMenu={(e) => {
              e.preventDefault();
              closeContextMenu();
            }}
          />

          <div
            className="ock-cloud__radialMenu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <button
              className="ock-cloud__radialItem ock-cloud__radialOpen"
              type="button"
              onClick={() => {
                const entry = contextMenu.entry;
                closeContextMenu();
                entry.type === "folder" ? openFolder(entry.path) : handlePreview(entry);
              }}
            >
              📂
              <span>Открыть</span>
            </button>

            {contextMenu.entry.type === "file" ? (
              <>
                <button
                  className="ock-cloud__radialItem ock-cloud__radialDownload"
                  type="button"
                  onClick={() => {
                    const entry = contextMenu.entry;
                    closeContextMenu();
                    handleDownload(entry);
                  }}
                >
                  ⬇️
                  <span>Скачать</span>
                </button>

                <button
                  className="ock-cloud__radialItem ock-cloud__radialRename"
                  type="button"
                  disabled={!canManage}
                  onClick={() => {
                    const entry = contextMenu.entry;
                    closeContextMenu();
                    startRename(entry);
                  }}
                >
                  ✏️
                  <span>Имя</span>
                </button>

                <button
                  className="ock-cloud__radialItem ock-cloud__radialDelete"
                  type="button"
                  disabled={!canManage}
                  onClick={() => {
                    const entry = contextMenu.entry;
                    closeContextMenu();
                    handleDeleteSingle(entry);
                  }}
                >
                  🗑️
                  <span>Удалить</span>
                </button>
              </>
            ) : null}

            <div className="ock-cloud__radialCenter">
              {contextMenu.entry.type === "folder" ? "📁" : "📄"}
            </div>
          </div>
        </>
      )}

      <OCKToasts />
    </div>
  );
}